import React, { useState, useRef, useEffect, ComponentType } from 'react';
import { CheckOutlined, CloseOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Space, Popconfirm, TablePaginationConfig } from "antd";
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { FilterConfirmProps } from "antd/es/table/interface";
import { InputRef } from "antd";
import axios from "axios";
import useSWR from "swr";
import { useAuth } from "../context/AuthContext";

export interface DataType {
    key: React.Key;
    id: number;
    item_code: string;
    item_name: string;
    location_code_rack: string;
    location_name_rack: string;
}

export interface RackLocationDataType {
    key: React.Key;
    id: React.Key;
    code_rack: string;
    name_rack: string;
    warehouse_code: string;
    status: string;
}

export interface WarehouseDataType {
    warehouse_code: string;
    warehouse_name: string;
}

const fetcher = (url: string) => axios.get(url).then(res => res.data);

export interface InjectedProps {
    showModal: () => void;
    isModalOpen: boolean;
    handleOk: () => void;
    handleCancel: () => void;
    isModalTwoOpen: boolean;
    showModalTwo: () => void;
    handleOkTwo: () => void;
    handleCancelTwo: () => void;
    form: any;
    dataSource: RackLocationDataType[];
    isLoading: boolean;
    pagination: TablePaginationConfig;
    rowSelection: { selectedRowKeys: React.Key[]; onChange: (keys: React.Key[]) => void };
    columns: ColumnsType<RackLocationDataType>;
    editingKey: React.Key;
    isAdding: boolean;
    selectedRowKeys: React.Key[];
    handleAdd: () => void;
    handleDelete: () => void;
    handleSubmit: () => void;
    handleTableChange: (pagination: TablePaginationConfig) => void;
    handleRowSelect: (record: DataType) => void;
    handleRowSelectTwo: (record: RackLocationDataType) => void;
}

const withItemLocationLogic = <P extends object>(WrappedComponent: ComponentType<P & InjectedProps>) => {
    const HOCComponent: React.FC<P> = (props) => {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [isModalTwoOpen, setIsModalTwoOpen] = useState(false);
        const [form] = Form.useForm();
        const { userData } = useAuth();
        const { data: warehouses } = useSWR<WarehouseDataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/warehouses`, fetcher);
        const { data: rackLocations, isLoading, mutate } = useSWR<RackLocationDataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/rack-locations`, fetcher);
        const [searchText, setSearchText] = useState('');
        const [searchedColumn, setSearchedColumn] = useState('');
        const searchInput = useRef<InputRef>(null);
        const [editingKey, setEditingKey] = useState<React.Key>('');
        const [isAdding, setIsAdding] = useState<boolean>(false);
        const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
        const [dataSource, setDataSource] = useState<RackLocationDataType[]>([]);
        const [pagination, setPagination] = useState<TablePaginationConfig>({
            current: 1,
            pageSize: 10,
        });

        useEffect(() => {
            if (rackLocations) {
                setDataSource(rackLocations);
            }
        }, [rackLocations]);

        const handleSearch = (
            selectedKeys: string[],
            confirm: (param?: FilterConfirmProps) => void,
            dataIndex: keyof RackLocationDataType,
        ) => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
        };

        const handleReset = (clearFilters: () => void) => {
            clearFilters();
            setSearchText('');
        };

        const getColumnSearchProps = (dataIndex: keyof RackLocationDataType): ColumnType<RackLocationDataType> => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        ref={searchInput}
                        placeholder={`Search ${dataIndex}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => clearFilters && handleReset(clearFilters)}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            close
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
            ),
            onFilter: (value, record) =>
                record[dataIndex]
                    .toString()
                    .toLowerCase()
                    .includes((value as string).toLowerCase()),
            onFilterDropdownOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
            render: (text) => text,
        });

        const handleAdd = () => {
            const newKey = 'new_rack_' + Math.random();
            const newRow: RackLocationDataType = {
                key: newKey,
                id: newKey,
                code_rack: '',
                name_rack: '',
                warehouse_code: userData?.warehouse_code || '',
                status: '1',
            };
            const currentPage = pagination.current || 1;
            const pageSize = pagination.pageSize || 10;
            const startIndex = (currentPage - 1) * pageSize;
            const newDataSource = [...dataSource];
            newDataSource.splice(startIndex, 0, newRow);
            setDataSource(newDataSource);
            form.setFieldsValue({ code_rack: '', name_rack: '' });
            setEditingKey(newRow.id);
            setIsAdding(true);
        };

        const handleDelete = async () => {
            try {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/rack-locations`, {
                    data: { keys: selectedRowKeys },
                });
                message.success(`${selectedRowKeys.length} item(s) deleted successfully.`);
                setSelectedRowKeys([]);
                mutate();
            } catch (error) {
                console.error("Failed to delete rack locations:", error);
                message.error("Failed to delete items.");
            }
        };

        const handleSave = async (record: RackLocationDataType) => {
            try {
                const row = await form.validateFields();
                const newData = { ...record, ...row };

                if (isAdding) {
                    if (!userData) {
                        message.error("User data is not available. Cannot add new rack.");
                        return;
                    }
                    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/rack-locations`, {
                        code_rack: newData.code_rack,
                        name_rack: newData.name_rack,
                        warehouse_code: userData.warehouse_code,
                    });
                    message.success('New rack added successfully');
                } else {

                    message.success(`Saved changes for ID: ${record.id}`);
                }
                setEditingKey('');
                setIsAdding(false);
                mutate();
            } catch (errInfo: any) {
                if (errInfo.response && errInfo.response.status === 409) {
                    message.error(errInfo.response.data.error);
                } else {
                    console.log('Validate Failed:', errInfo);
                    message.error('Failed to save. Please check the form fields.');
                }
            }
        };

        const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(newSelectedRowKeys);
        };

        const rowSelection = {
            selectedRowKeys,
            onChange: onSelectChange,
        };

        const columns: ColumnsType<RackLocationDataType> = [
            {
                title: 'Rack Location',
                dataIndex: 'code_rack',
                key: 'code_rack',
                sorter: (a, b) => a.code_rack.localeCompare(b.code_rack),
                ...getColumnSearchProps('code_rack'),
                render: (text, record) => {
                    if (record.id === editingKey) {
                        return (
                            <Form.Item name="code_rack" style={{ margin: 0 }} rules={[{ required: true, message: 'Please input rack code!' }]}>
                                <Input />
                            </Form.Item>
                        );
                    }
                    return text;
                }
            },
            {
                title: 'Rack Location Name',
                dataIndex: 'name_rack',
                key: 'name_rack',
                sorter: (a, b) => a.name_rack.localeCompare(b.name_rack),
                ...getColumnSearchProps('name_rack'),
                render: (text, record) => {
                    if (record.id === editingKey) {
                        return (
                            <Form.Item name="name_rack" style={{ margin: 0 }} rules={[{ required: true, message: 'Please input rack name!' }]}>
                                <Input />
                            </Form.Item>
                        );
                    }
                    return text;
                }
            },
            {
                title: 'Warehouse',
                dataIndex: 'warehouse_code',
                key: 'warehouse_code',
                sorter: (a, b) => (a.warehouse_code || '').localeCompare(b.warehouse_code || ''),
                ...getColumnSearchProps('warehouse_code'),
                render: (text, record) => {
                    if (record.id === editingKey && isAdding) {
                        const warehouse = warehouses?.find(wh => wh.warehouse_code === userData?.warehouse_code);
                        return <span>{warehouse ? warehouse.warehouse_name : userData?.warehouse_code}</span>;
                    }
                    const warehouse = warehouses?.find(wh => wh.warehouse_code === text);
                    return warehouse ? warehouse.warehouse_name : text;
                }
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                sorter: (a, b) => a.status.localeCompare(b.status),
                ...getColumnSearchProps('status'),
                render: (text: string) => {
                    if (String(text) === '1') {
                        return 'Active';
                    }
                    if (String(text) === '2') {
                        return 'Deactive';
                    }
                    return text;
                },
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, record) => {
                    const isEditing = record.id === editingKey;
                    if (isEditing) {
                        return (
                            <Space size="middle">
                                <Popconfirm
                                    title="Simpan data baru?"
                                    onConfirm={() => handleSave(record)}
                                    okText="Ya"
                                    cancelText="Tidak"
                                >
                                    <Button type="link" icon={<CheckOutlined />} style={{ padding: 0 }} />
                                </Popconfirm>
                                <Button
                                    type="link"
                                    icon={<CloseOutlined />}
                                    onClick={() => {
                                        if (isAdding) {
                                            setDataSource(dataSource.filter(item => item.id !== record.id));
                                        }
                                        setEditingKey('');
                                        setIsAdding(false);
                                    }}
                                    style={{ padding: 0 }}
                                />
                            </Space>
                        );
                    }
                    return (
                        <Button
                            type="link"
                            disabled={editingKey !== '' || selectedRowKeys.length > 0}
                            onClick={() => {
                                form.setFieldsValue({ ...record });
                                setEditingKey(record.id);
                            }}
                            icon={<EditOutlined />}
                            style={{ padding: 0 }}
                        />
                    );
                },
            },
        ];

        useEffect(() => {
            if (userData?.warehouse_code && warehouses) {
                const currentWarehouse = warehouses.find(
                    (wh) => wh.warehouse_code === userData.warehouse_code
                );

                if (currentWarehouse) {
                    form.setFieldsValue({
                        warehouse: `${currentWarehouse.warehouse_name}`,
                    });
                } else {
                    form.setFieldsValue({
                        warehouse: userData.warehouse_code,
                    });
                }
            }
        }, [userData, form, warehouses]);

        const handleRowSelect = (record: DataType) => {
            form.setFieldsValue({
                item_code_from: record.item_code,
                item_code_to: record.item_code,
                item_name_from: record.item_name,
                item_name_to: record.item_name,
                rack_location_code_from: record.location_code_rack,
                rack_location_name_from: record.location_name_rack,
            });
            setIsModalOpen(false);
        };

        const handleRowSelectTwo = (record: RackLocationDataType) => {
            form.setFieldsValue({
                rack_location_code: record.code_rack,
                rack_location_name_to: record.name_rack,
            });
            setIsModalTwoOpen(false);
        };

        const handleSubmit = async () => {
            try {
                const values = await form.validateFields();
                const { item_code_from, rack_location_code, item_name_from, rack_location_code_from, rack_location_name_from, rack_location_name_to } = values;

                if (!item_code_from || !rack_location_code) {
                    message.error("Please select an item and a new rack location first.");
                    return;
                }

                if (!userData?.warehouse_code) {
                    message.error("Warehouse information is missing. Cannot submit.");
                    return;
                }

                const payload = {
                    item_code: item_code_from,
                    new_location_code: rack_location_code,
                    warehouse_code: userData.warehouse_code,
                };

                await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/item-locations`, payload);

                const today = new Date();
                const day = String(today.getDate()).padStart(2, '0');
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const year = today.getFullYear();
                const formattedDate = `${day}/${month}/${year}`;

                const historyPayload = {
                    date: formattedDate,
                    warehouse: userData.warehouse_code,
                    item_name: item_name_from,
                    item_code: item_code_from,
                    rack_location_code: rack_location_code_from,
                    rack_location_name: rack_location_name_from,
                    rack_location_code_change: rack_location_code,
                    rack_location_name_change: rack_location_name_to,
                };

                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/item-location-history`, historyPayload);

                message.success("Item location updated successfully!");
                form.resetFields();
            } catch (error) {
                console.error("Failed to update item location:", error);
                message.error("Failed to update item location. Please check the console for details.");
            }
        };

        const handleTableChange = (newPagination: TablePaginationConfig) => {
            setPagination(newPagination);
        };

        const showModal = () => {
            setIsModalOpen(true);
        };

        const handleOk = () => {
            setIsModalOpen(false);
        };

        const handleCancel = () => {
            setIsModalOpen(false);
        };

        const showModalTwo = () => {
            setIsModalTwoOpen(true);
        };

        const handleOkTwo = () => {
            setIsModalTwoOpen(false);
        };

        const handleCancelTwo = () => {
            setIsModalTwoOpen(false);
        };

        return (
            <>
                <WrappedComponent
                    {...props as P}
                    showModal={showModal}
                    isModalOpen={isModalOpen}
                    handleOk={handleOk}
                    handleCancel={handleCancel}
                    isModalTwoOpen={isModalTwoOpen}
                    showModalTwo={showModalTwo}
                    handleOkTwo={handleOkTwo}
                    handleCancelTwo={handleCancelTwo}
                    form={form}
                    dataSource={dataSource}
                    isLoading={isLoading}
                    pagination={pagination}
                    rowSelection={rowSelection}
                    columns={columns}
                    editingKey={editingKey}
                    isAdding={isAdding}
                    selectedRowKeys={selectedRowKeys}
                    handleAdd={handleAdd}
                    handleDelete={handleDelete}
                    handleSubmit={handleSubmit}
                    handleTableChange={handleTableChange}
                    handleRowSelect={handleRowSelect}
                    handleRowSelectTwo={handleRowSelectTwo}
                />
            </>
        );
    };

    return HOCComponent;
};

export default withItemLocationLogic;